import { Map, Record, List } from "immutable";
import { LevelRecord, levelFromJSON } from "common/records/Level";
import { RootActions, RootState } from "common/reducers";
import { fetch, loaded, receive, reset } from "common/reducers/Utils";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import {
  ACTIVATE_LEVEL,
  DESTROY_LEVEL,
  FETCH_LEVEL,
  LevelActions,
  RECEIVE_LEVEL,
  RECEIVE_LEVELS,
  REMOVE_LEVEL,
  RESET_LEVEL,
  SET_LEVEL_LOADED,
} from "common/reducers/level.actions";

export const LevelStateRecord = Record({
  byId: Map<string | number, LevelRecord>(),
  lastSavedById: Map<string | number, LevelRecord>(),
});

export const initialState = LevelStateRecord();

export type LevelState = typeof initialState;

export const model = "levels";

export const reducer = (state: RootState, action: RootActions): RootState => {
  if (state && !state.get(model)) {
    state = state.set(model, initialState);
  }

  switch (action.type) {
    case FETCH_LEVEL:
      return fetch(state, model, levelFromJSON({ id: action.payload.levelId })) as RootState;

    case RECEIVE_LEVELS:
      const levels = List(action.payload.levels);
      levels.forEach((level) => {
        state = reducer(state, LevelActions.receiveLevel(level));
      });

      const activeIds = levels
        .filter((level) => Boolean(level.org_level_link))
        .sortBy((level) => level.org_level_link?.sort_order)
        .map((level) => level.id);
      const availableIds = levels.filter((level) => !level.org_level_link).map((level) => level.id);

      state = idsManagementReducer(state, IdsManagementActions.receiveModel(model + ".active", activeIds));
      state = idsManagementReducer(state, IdsManagementActions.receiveModel(model + ".available", availableIds));

      return state.setIn([model, "loading"], false);

    case RECEIVE_LEVEL:
      const levelRecord = levelFromJSON(action.payload.level);
      state = receive(state, model, levelRecord) as RootState;

      if (action.payload.pushToList) {
        state = idsManagementReducer(state, IdsManagementActions.pushId(levelRecord.id, model + ".active"));
      }
      return state;

    case RESET_LEVEL:
      return reset(state, model, action.payload.levelId) as RootState;

    case SET_LEVEL_LOADED:
      return loaded(state, model, action.payload.levelId) as RootState;

    case DESTROY_LEVEL:
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.levelId, model + ".available"));

      return state
        .deleteIn([model, "byId", action.payload.levelId])
        .deleteIn([model, "lastSavedById", action.payload.levelId]);

    case REMOVE_LEVEL: {
      state = idsManagementReducer(state, IdsManagementActions.pushId(action.payload.levelId, model + ".available"));
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.levelId, model + ".active"));

      return state.updateIn([model, "byId", action.payload.levelId], (level: LevelRecord) =>
        level.set("org_level_link", null),
      );
    }
    case ACTIVATE_LEVEL: {
      state = idsManagementReducer(state, IdsManagementActions.pushId(action.payload.levelId, model + ".active"));
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.levelId, model + ".available"));

      return state;
    }

    default:
      return state;
  }
};
